import React from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import Plotting from "../Plotting";

function formatMetric(value) {
  const lowerThreshold = 1e-5;
  const upperThreshold = 1e5;

  if (Math.abs(value) < lowerThreshold || Math.abs(value) > upperThreshold) {
    return value.toExponential(2); // Formats in scientific notation with 2 decimal places
  }

  return value.toFixed(2); // Formats as a regular number with 2 decimal places
}

export default function AnalyzeRegression(props) {
  const theme = useTheme();
  // init internationalization
  const { t } = useTranslation();

  return props.runData.predictionDetails.targetListTrain ? (
    <>
      <Grid item xl={5} md={12} sm={12} sx={{ marginTop: theme.spacing(4) }}>
        <TableContainer component={Paper}>
          <Table aria-label="caption table">
            <caption>
              nRMSE = Normalized Root Mean Squared Error
              <br />
              MAE = Mean Absolute Error
            </caption>
            <TableHead>
              <TableRow>
                <TableCell align="left">
                  {t("analyzer.RegressionMetricTitle")}
                </TableCell>
                <TableCell align="left">
                  {t("analyzer.RegressionMetricTableValLabel")}
                </TableCell>
                <TableCell align="left">
                  {t("analyzer.RegressionMetricTableTrainLabel")}
                </TableCell>
                {props.runData.metrics.MAETest && (
                  <TableCell align="left">
                    {t("analyzer.RegressionMetricTableTestLabel")}
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow key="row-idx-0">
                <TableCell align="left">nRMSE</TableCell>
                <TableCell align="left">
                  {formatMetric(props.runData.metrics.nRMSEVal)} %
                </TableCell>
                <TableCell align="left">
                  {formatMetric(props.runData.metrics.nRMSETrain)} %
                </TableCell>
                {props.runData.metrics.nRMSETest && (
                  <TableCell align="left">
                    {formatMetric(props.runData.metrics.nRMSETest)} %
                  </TableCell>
                )}
              </TableRow>
              <TableRow key="row-idx-1">
                <TableCell align="left">MAE</TableCell>
                <TableCell align="left">
                  {formatMetric(props.runData.metrics.MAEVal)}
                </TableCell>
                <TableCell align="left">
                  {formatMetric(props.runData.metrics.MAETrain)}
                </TableCell>
                {props.runData.metrics.MAETest && (
                  <TableCell align="left">
                    {formatMetric(props.runData.metrics.MAETest)}
                  </TableCell>
                )}
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
      <Grid item xl={9} xs={6} sx={{ marginTop: theme.spacing(4) }} />
      <Grid item xl={12} xs={12} sx={{ marginTop: theme.spacing(4) }}>
        <Grid container spacing={2} row-spacing={6}>
          <Grid item xl={8} md={12} sm={12}>
            <Card>
              <Plotting
                runData={props.runData}
                type="scatterAnalyze"
                selectedTargetIndex={props.selectedTargetIndex}
              />
            </Card>
          </Grid>
          <Grid item xl={4} md={12} sm={12}>
            <Card sx={{ backgroundColor: theme.palette.background.default }}>
              <CardContent>
                <Typography gutterBottom variant="h5" component="div">
                  Scatter Plot
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("analyzer.DescriptionScatterPlot")}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xl={8} md={12} sm={12}>
            <Card>
              <Plotting
                runData={props.runData}
                type="boxAnalyze"
                selectedTargetIndex={props.selectedTargetIndex}
              />
            </Card>
          </Grid>
          <Grid item xl={4} md={12} sm={12}>
            <Card sx={{ backgroundColor: theme.palette.background.default }}>
              <CardContent>
                <Typography gutterBottom variant="h5" component="div">
                  Box Plot
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("analyzer.DescriptionBoxPlot")}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          {Object.keys(props.runData.featureImportance).length ? (
            <>
              <Grid item xl={8} md={12} sm={12}>
                <Card>
                  <Plotting
                    runData={props.runData}
                    type="featureImportance"
                    selectedTargetIndex={props.selectedTargetIndex}
                  />
                </Card>
              </Grid>
              <Grid item xl={4} md={12} sm={12}>
                <Card
                  sx={{ backgroundColor: theme.palette.background.default }}
                >
                  <CardContent>
                    <Typography gutterBottom variant="h5" component="div">
                      Feature Importance Plot
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t("analyzer.DescriptionFeatureImportance")}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </>
          ) : (
            ""
          )}
          <Grid item lg={8} xs={12}>
            <Card>
              <Typography
                gutterBottom
                variant="h6"
                sx={{
                  textAlign: "center",
                  marginTop: "25px",
                  fontSize: "16px",
                  fontWeight: 400,
                  color: theme.palette.secondary.dark,
                }}
              >
                {t("analyzer.LabelSHAPPlot")}
              </Typography>
              <img
                alt="SHAP Plot"
                width={600}
                src={`data:image/svg+xml;base64,${btoa(
                  unescape(encodeURIComponent(props.runData.SHAPBeeswarmPlot)),
                )}`}
                style={{ margin: "20px" }}
              />
            </Card>
          </Grid>
          <Grid item xl={4} md={12} sm={12}>
            <Card sx={{ backgroundColor: theme.palette.background.default }}>
              <CardContent>
                <Typography gutterBottom variant="h5" component="div">
                  SHAP Plot
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("analyzer.DescriptionSHAP")}
                </Typography>
                <br />
                <a
                  href="https://github.com/shap/shap/tree/master"
                  rel="noreferrer"
                  target="_blank"
                >
                  🔗 {t("common.LearnMoreButton")}
                </a>
                <br />
                <a
                  href="https://www.aidancooper.co.uk/content/images/size/w1600/2021/11/beeswarm-1.png?xgtab&"
                  rel="noreferrer"
                  target="_blank"
                >
                  🔗 {t("analyzer.ReadMoreSHAPExplanation")}
                </a>
              </CardContent>
            </Card>
          </Grid>

          <Grid container spacing={2} row-spacing={6}>
            {props.runData?.dataBalancer?.binValue?.length > 0 && (
                <>
                  <Grid item lg={4} xs={12} order={{ xs: 1, md: 1, lg: 2 }}>
                    <Card
                      sx={{
                        backgroundColor: theme.palette.background.default,
                      }}
                    >
                      <CardContent>
                        <Typography gutterBottom variant="h5" component="div">
                          {t("analyzer.DataBalancingTitle")}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t("analyzer.DescriptionDataBalancing")}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item lg={8} xs={12} order={{ xs: 2, md: 2, lg: 1 }}>
                    <Card>
                      <Plotting runData={props.runData} type="dataBalancer" />
                    </Card>
                  </Grid>
                </>
              )}
          </Grid>
        </Grid>
      </Grid>
    </>
  ) : (
    ""
  );
}
